import React, { useCallback, useEffect, useMemo } from 'react';
import { MenuItemProps } from '@televet/kibble-ui/build/components/Menu';
import { Select } from '@televet/kibble-ui/build/components/Select';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import { useGetAppointmentModalAppointmentTypesLazyQuery } from 'shared/types/graphql';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { updateAppointmentDraft, updateAppointmentModalErrors } from 'pages/Appointments/state/appointmentsModalSlice';
import useClinicUser from 'shared/hooks/useClinicUser';
import * as Sentry from '@sentry/react';
import { LinkedAutomationUpdates } from '..';

const DEFAULT_OPTION: MenuItemProps = { label: 'None', value: 'None' };

interface AppointmentTypeProps {
  updateLinkedAutomations: (updates: LinkedAutomationUpdates) => Promise<void>;
}

export const AppointmentType = ({ updateLinkedAutomations }: AppointmentTypeProps): JSX.Element => {
  const { currentClinicId } = useClinicUser();
  const { appointmentDraft, appointmentId, errors } = useAppSelector((state) => state.appointmentsModal);
  const dispatch = useAppDispatch();
  const toast = useToast();

  const [getAppointmentTypes, { data: appointmentTypesData, loading: isLoadingAppointmentTypes }] =
    useGetAppointmentModalAppointmentTypesLazyQuery({
      variables: { clinicId: currentClinicId || '' },
      onError: (e) => {
        toast({
          title: 'Sorry!',
          description: 'We are having trouble finding your appointment types',
          status: 'error',
        });
        Sentry.captureException(e);
      },
    });

  const appointmentTypesOptions: MenuItemProps[] = useMemo(() => {
    if (appointmentTypesData?.appointmentTypes) {
      return [DEFAULT_OPTION].concat(
        appointmentTypesData.appointmentTypes.map((appointmentType) => {
          return {
            label: appointmentType.name,
            value: appointmentType.id,
            isSelected: appointmentType.id === appointmentDraft?.appointmentTypeId,
          };
        }),
      );
    }
    return [];
  }, [appointmentDraft?.appointmentTypeId, appointmentTypesData?.appointmentTypes]);

  const handleUpdateAppointmentType = useCallback(
    (selectedItem) => {
      dispatch(
        updateAppointmentDraft({
          appointmentTypeId: selectedItem.value === 'None' ? null : selectedItem.value,
        }),
      );
      dispatch(updateAppointmentModalErrors(errors.filter((error) => error !== 'missingAppointmentType')));
      updateLinkedAutomations({ appointmentTypeId: selectedItem.value });
    },
    [dispatch, errors, updateLinkedAutomations],
  );

  const handleOnMenuOpen = useCallback(async () => {
    if (appointmentId) return;
    await getAppointmentTypes();
  }, [appointmentId, getAppointmentTypes]);

  useEffect(() => {
    // If editing appointment, fetch appointment types on load so we can render selected value
    if (appointmentId) {
      getAppointmentTypes();
    }
  }, [appointmentId, getAppointmentTypes]);

  return (
    <Select
      className="AppointmentModal__AppointmentType"
      data-testid="appointment-type-select"
      listProps={{
        isSearchable: true,
        onSelect: handleUpdateAppointmentType,
        maxH: '300px',
        isLoading: isLoadingAppointmentTypes,
      }}
      containerProps={{
        onOpen: handleOnMenuOpen,
      }}
      labelStyle={{ fontWeight: 'bold' }}
      value={appointmentDraft?.appointmentTypeId}
      placeholder="Select an appointment type"
      label="Appointment Type"
      options={appointmentTypesOptions}
      isInvalid={errors.includes('missingAppointmentType')}
      errorText="Please select an appointment type"
    />
  );
};
